import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useFormContext, Controller } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { MenuItem, TextField, Box } from '@material-ui/core';
import _ from 'lodash';

import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import NgpTextField from '../../form/NgpTextField';
import NgpToggle from '../../form/NgpToggle';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpNativeNumberField from '../../form/NgpNativeNumberField';
import {
    METRIC_COLOR_SCALE,
    getNextChartColor,
    getNextMetricId,
    makeMetricNamePretty,
} from './TestTypesDetailPageHelper';

const METRIC_NAME_FIELD = 'name';
const METRIC_TITLE_FIELD = 'title';
const METRIC_UNITS_FIELD = 'units';
const METRIC_MULTIPLIER_FIELD = 'multiplier';
const METRIC_IS_STRING_FIELD = 'isString';
const METRIC_IS_MAIN_FIELD = 'isMain';
const METRIC_HIDDEN_FIELD = 'hidden';
const METRIC_COLOR_FIELD = 'color';
const METRIC_CHART_TYPE_FIELD = 'chartSeriesOverrides.type';
const METRIC_Y_AXIS_FIELD = 'chartSeriesOverrides.yAxis';

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

function fixMetrics(metrics, metric) {
    if (!metric.id) {
        metric.id = getNextMetricId(metrics);
    }
    if (metric.isString === true) {
        metric.isMain = false;
        metric.hidden = true;
        metric.color = '';
        metric.units = '';
        metric.multiplier = 0;
    }
    // Make sure we only have one main metric selected
    if (metric.isMain === true) {
        _.each(
            _.filter(metrics, (aMetric) => aMetric.id !== metric.id),
            (thisMetric) => {
                thisMetric.isMain = false;
            }
        );
    }
    return metric;
}

const RESERVED_METRICS = [
    'availability',
    'resolved_ip_address',
    'message',
    'status',
    'ispass',
    'statuscounts',
    'wifi_ssid',
];
const METRIC_NAME_REGEX = /^[a-zA-Z_0-9]*$/;

const DEFAULT_CHART_TYPES = ['area', 'line', 'scatter'];
const CHART_TYPE_LABELS = {
    default: 'Default',
    area: 'Area',
    line: 'Line',
    column: 'Bar',
    scatter: 'Scatter Plot',
};

function hasAnotherPrimaryMetric(metrics = EMPTY_ARRAY, currentMetric = EMPTY_OBJECT) {
    const currentInternalId = currentMetric?.[INTERNAL_ID_FIELD];

    return _.some(metrics, (metric) => {
        return !!metric?.[METRIC_IS_MAIN_FIELD] && metric?.[INTERNAL_ID_FIELD] !== currentInternalId;
    });
}

/**
 * Normalize chartSeriesOverrides.type for the form Select:
 * null/undefined → 'null' so it matches <MenuItem value="null">.
 * On submit, 'null' is converted back to real null.
 */
function normalizeChartOverrides(overrides) {
    const base = { ...defaultFormValues.chartSeriesOverrides, ...overrides };
    if (base.type == null) {
        base.type = 'null';
    }
    return base;
}

const defaultFormValues = {
    [METRIC_NAME_FIELD]: '',
    [METRIC_TITLE_FIELD]: '',
    [METRIC_UNITS_FIELD]: '',
    [METRIC_MULTIPLIER_FIELD]: 0,
    [METRIC_IS_STRING_FIELD]: false,
    [METRIC_IS_MAIN_FIELD]: true,
    [METRIC_HIDDEN_FIELD]: false,
    [METRIC_COLOR_FIELD]: null,
    chartSeriesOverrides: {
        type: 'null',
        yAxis: 0,
        zIndex: 0,
    },
};

function MetricOptionsFields({ chart2axis, chartTypes }) {
    const { watch, setValue, control } = useFormContext();
    const isMainMetric = !!watch(METRIC_IS_MAIN_FIELD);
    const isHiddenMetric = !!watch(METRIC_HIDDEN_FIELD);
    const isStringMetric = !!watch(METRIC_IS_STRING_FIELD);

    useEffect(() => {
        // Main metric should always be visible.
        if (isMainMetric && isHiddenMetric) {
            setValue(METRIC_HIDDEN_FIELD, false, { shouldDirty: true });
        }
    }, [isMainMetric, isHiddenMetric, setValue]);

    return (
        <>
            {!isStringMetric && (
                <>
                    <NgpTextField
                        fieldName={METRIC_UNITS_FIELD}
                        label={<Trans>Metric Units Label</Trans>}
                        inputProps={{ maxLength: 5, size: 20 }}
                        helpTipText={t`The Metric Units Label (i.e. ms, %, or Mbps) will show up next to the collected metric on the Service Test results screens`}
                    />
                    <NgpNativeNumberField
                        fieldName={METRIC_MULTIPLIER_FIELD}
                        label={<Trans>Metric Precision</Trans>}
                        min={0}
                        inputWidth={'20ch'}
                        helpTipText={t`The decimal precision for this metric.  If this is changed after tests have run, only new results will have the updated precision.`}
                    />

                    <NgpToggle
                        fieldName={METRIC_HIDDEN_FIELD}
                        label={<Trans>Show on performance charts</Trans>}
                        inverted
                        helpTipText={t`If selected, this metric will be plotted on the performance charts on the Service Test result screens`}
                    />

                    {!isHiddenMetric && (
                        <Controller
                            control={control}
                            name={METRIC_COLOR_FIELD}
                            render={({ value, onChange }) => (
                                <TextField
                                    type="color"
                                    label={t`Color`}
                                    value={value || METRIC_COLOR_SCALE[0]}
                                    onChange={onChange}
                                    InputLabelProps={{ shrink: true }}
                                    style={{ minWidth: 160 }}
                                    inputProps={{
                                        list: 'ngpPalette',
                                        style: {
                                            width: 112,
                                            height: 30,
                                            padding: 2,
                                            cursor: 'pointer',
                                        },
                                    }}
                                />
                            )}
                        />
                    )}
                    <datalist id="ngpPalette">
                        {METRIC_COLOR_SCALE.map((color) => (
                            <option key={color} value={color} />
                        ))}
                    </datalist>

                    {!isHiddenMetric && (
                        <NgpTextField fieldName={METRIC_CHART_TYPE_FIELD} label={<Trans>Chart Type</Trans>} select>
                            <MenuItem value="null">{CHART_TYPE_LABELS.default}</MenuItem>
                            {(chartTypes || []).map((chartType) => (
                                <MenuItem key={chartType} value={chartType}>
                                    {CHART_TYPE_LABELS[chartType] || _.startCase(chartType)}
                                </MenuItem>
                            ))}
                        </NgpTextField>
                    )}

                    {!isHiddenMetric && chart2axis && (
                        <NgpTextField fieldName={METRIC_Y_AXIS_FIELD} label={<Trans>Axis</Trans>} select>
                            <MenuItem value={0}>{t`Left Axis`}</MenuItem>
                            <MenuItem value={1}>{t`Right Axis`}</MenuItem>
                        </NgpTextField>
                    )}
                </>
            )}
        </>
    );
}
const formId = 'metricsDialogForm';
export default function MetricsDialogForm({
    selectedList = EMPTY_ARRAY,
    selectedRecord = EMPTY_OBJECT,
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
    chart2axis = false,
    chartTypes = DEFAULT_CHART_TYPES,
}) {
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: defaultFormValues, mode: 'onChange' });
    const { handleSubmit, formState, reset, setError, getValues, setValue, watch } = methods;
    const internalIdRef = useRef(null);
    const titlePreviouslySetRef = useRef(false);
    const isStringMetric = !!watch(METRIC_IS_STRING_FIELD);
    const hasPrimaryMetric = hasAnotherPrimaryMetric(selectedList, selectedRecord);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (!_.isEmpty(selectedRecord)) {
            internalIdRef.current = selectedRecord[INTERNAL_ID_FIELD] || null;
            const selectedColor = `${selectedRecord?.[METRIC_COLOR_FIELD] || ''}`.trim();
            const initializedColor = _.isEmpty(selectedColor)
                ? getNextChartColor(selectedList, internalIdRef.current)
                : selectedColor;

            reset({
                ...defaultFormValues,
                ...selectedRecord,
                [METRIC_COLOR_FIELD]: initializedColor,
                chartSeriesOverrides: normalizeChartOverrides(selectedRecord?.chartSeriesOverrides),
            });
            titlePreviouslySetRef.current = !_.isEmpty(`${selectedRecord?.[METRIC_TITLE_FIELD] || ''}`.trim());
            return;
        }

        internalIdRef.current = null;
        titlePreviouslySetRef.current = false;
        reset({
            ...defaultFormValues,
            [METRIC_IS_MAIN_FIELD]: !hasPrimaryMetric,
            [METRIC_COLOR_FIELD]: getNextChartColor(selectedList),
        });
    }, [hasPrimaryMetric, open, reset, selectedList, selectedRecord]);

    const handleCleanup = () => {
        internalIdRef.current = null;
        titlePreviouslySetRef.current = false;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleDialogSubmit = (dataToSave = {}) => {
        let nextData = {
            ...dataToSave,
            [METRIC_NAME_FIELD]: `${dataToSave?.[METRIC_NAME_FIELD] || ''}`.trim(),
            [METRIC_TITLE_FIELD]: `${dataToSave?.[METRIC_TITLE_FIELD] || ''}`.trim(),
        };

        if (_.get(nextData, METRIC_CHART_TYPE_FIELD) === 'null') {
            _.set(nextData, METRIC_CHART_TYPE_FIELD, null);
        }

        if (internalIdRef.current) {
            _.set(nextData, INTERNAL_ID_FIELD, internalIdRef.current);
        }

        const hasDuplicateName = _.some(selectedList, (item) => {
            return (
                _.toLower(item?.[METRIC_NAME_FIELD]) === _.toLower(nextData?.[METRIC_NAME_FIELD]) &&
                item?.[INTERNAL_ID_FIELD] !== nextData?.[INTERNAL_ID_FIELD]
            );
        });

        if (hasDuplicateName) {
            setError(METRIC_NAME_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: t`Metric name must be unique`,
            });
            return;
        }

        if (!METRIC_NAME_REGEX.test(nextData?.[METRIC_NAME_FIELD] || '')) {
            setError(METRIC_NAME_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: t`Only letters, numbers and underscores are allowed`,
            });
            return;
        }

        if (RESERVED_METRICS.includes(_.toLower(nextData?.[METRIC_NAME_FIELD] || ''))) {
            setError(METRIC_NAME_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: t`This metric name is reserved. Please choose a different name.`,
            });
            return;
        }
        nextData = fixMetrics(selectedList, nextData);

        return Promise.resolve(onConfirm(nextData)).then(handleCleanup);
    };

    return (
        <NgpTwoButtonDialog
            title={<Trans>Service Test Metric</Trans>}
            confirmButtonText={<Trans>ADD</Trans>}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleDialogSubmit)}
        >
            <Box width={600}>
                <FormProvider {...methods}>
                    <NgpForm
                        formId={formId}
                        initialized
                        loading={false}
                        showSave={false}
                        formPadding={false}
                        showRequiredFieldsTip={false}
                        submitFunction={handleDialogSubmit}
                    >
                        <FormSection>
                            <NgpToggle
                                fieldName={METRIC_IS_STRING_FIELD}
                                label={<Trans>Metric data is a string</Trans>}
                            />
                            {!isStringMetric && (
                                <NgpToggle
                                    helpTipText={t`The primary metric will be aggregated and show up on the Service Test result screens`}
                                    fieldName={METRIC_IS_MAIN_FIELD}
                                    label={<Trans>Primary Test Metric</Trans>}
                                    onChangeFunc={(e, checked) => {
                                        if (checked) {
                                            setValue(METRIC_HIDDEN_FIELD, false, { shouldDirty: true });
                                        }
                                    }}
                                />
                            )}
                            <NgpTextField
                                fieldName={METRIC_NAME_FIELD}
                                label={<Trans>Metric Name</Trans>}
                                required
                                inputProps={{ maxLength: 35, size: 35 }}
                                helpTipText={t`Metric names can only be letters, numbers and underscores. An example metric name would be "test_output_metric"`}
                                onChangeFunc={(e) => {
                                    const name = `${e?.target?.value || ''}`.trim();
                                    const currentTitle = `${getValues(METRIC_TITLE_FIELD) || ''}`.trim();
                                    const shouldAutofill = _.isEmpty(currentTitle) || !titlePreviouslySetRef.current;
                                    if (!_.isEmpty(name) && shouldAutofill) {
                                        setValue(METRIC_TITLE_FIELD, makeMetricNamePretty(name), {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }
                                }}
                                fieldRules={{
                                    validate: {
                                        badFormat: (value) =>
                                            METRIC_NAME_REGEX.test(value || '') ||
                                            t`Only letters, numbers and underscores are allowed`,
                                        restricted: (value) =>
                                            !RESERVED_METRICS.includes(_.toLower(value || '')) ||
                                            t`This metric name is reserved. Please choose a different name.`,
                                        duplicate: (value) => {
                                            const normalized = _.toLower(`${value || ''}`);
                                            const hasDuplicate = _.some(selectedList, (item) => {
                                                return (
                                                    _.toLower(item?.[METRIC_NAME_FIELD]) === normalized &&
                                                    item?.[INTERNAL_ID_FIELD] !== internalIdRef.current
                                                );
                                            });

                                            return !hasDuplicate || t`A metric already exists with this name`;
                                        },
                                    },
                                }}
                            />
                            <NgpTextField
                                fieldName={METRIC_TITLE_FIELD}
                                label={<Trans>Metric Label</Trans>}
                                required
                                inputProps={{ maxLength: 35, size: 35 }}
                                onChangeFunc={() => {
                                    const currentTitle = `${getValues(METRIC_TITLE_FIELD) || ''}`.trim();
                                    titlePreviouslySetRef.current = !_.isEmpty(currentTitle);
                                }}
                                helpTipText={t`The Metric Label will be the friendly name that is used on the Service Test result screens`}
                            />

                            <MetricOptionsFields
                                chart2axis={chart2axis}
                                chartTypes={chartTypes}
                                isStringMetric={isStringMetric}
                            />
                        </FormSection>
                    </NgpForm>
                </FormProvider>
            </Box>
        </NgpTwoButtonDialog>
    );
}
